import { AxiosProgressEvent } from 'axios';
import API from '../api/index';
import { IRectificativaPayload } from '../components/facturacion/facturarBono/RectificarFacturaBono';
import { IFacturaMetodoPago } from '../components/facturacion/facturarCita/FacturarCitaDialog';
import { IFactura } from '../components/facturacion/Facturas';
import { FacturaInforme } from '../components/facturacion/informes/Informes';
import { INoFacturable } from '../components/facturacion/ServiciosNoFacturables';
import { ISinFacturar } from '../components/facturacion/ServiciosSinFacturar';
import IServiceClassInvoice from '../types/ServiceClassInvoice';
import { IFacturaComplementariaPayload, IFacturaRectificativaPayload, IFacturaPayload, IFacturaClasePayload, IFacturaManualPayload } from '../utils/Factura';
import { IInvoiceCounterEntidad } from '../types/EntidadFacturacion';

export interface IInvoiceCounterPayload {
    userId: string;
    serie: string;
    counter: number;
}

interface IUpdateInvoiceCounterPayload {
    entidadId?: string | null;
    newSerie: string;
    counter: number;
}

const getInvoiceCounter = (userId: string) => API.get<IInvoiceCounterPayload>(`/invoiceCounter/${userId}`);
const getInvoiceCounters = (userId: string) => API.get<IInvoiceCounterEntidad[]>(`/invoiceCounters/${userId}`);
const createInvoiceCounter = (data: IInvoiceCounterPayload) => API.post<IInvoiceCounterPayload>('/invoiceCounter', data);
const updateInvoiceCounter = (userId: string, data: IUpdateInvoiceCounterPayload) => API.put<IInvoiceCounterPayload>(`/invoiceCounter/${userId}`, data);

const checkActiveServiceInvoice = (citaId: string) => API.get(`/serviceInvoice/checkActive/${citaId}`);
const checkOriginalServiceInvoice = (servicioId: string) => API.get(`/serviceInvoice/checkOriginal/${servicioId}`);
const marcarNoFacturable = (userId: string, servicioId: string, pacienteId: string, motivoNoFacturable: string) => API.post('/serviceInvoice/no-facturable', { userId, servicioId, pacienteId, motivoNoFacturable });
const updateMotivoNoFacturable = (id: string, motivoNoFacturable: string) => API.put(`/serviceInvoice/no-facturable/${id}`, { motivoNoFacturable });
const createFactura = (data: IFacturaPayload) => API.post('/facturas', data);
const editFactura = (_id: string, data: IFacturaPayload) => API.put(`/facturas/${_id}`, data);
const pagarFactura = (_id: string, data: { tipoPago: string, metodoPago: IFacturaMetodoPago, servicioId?: string, pacienteId?: string, bonoId?: string }) => API.patch(`/facturas/${_id}/pagar`, data);
const getNumeroOrientativo = (_id: string) => API.get<{ numero: string | null; definitivo?: boolean; aviso?: string }>(`/facturas/${_id}/numero-orientativo`);
const eliminarFactura = (_id: string) => API.delete(`/facturas/${_id}`);
const emitirFactura = (_id: string, data: { userId: string, tipoPago: string, servicioId?: string, pacienteId?: string }) => API.patch(`/facturas/${_id}/emitir`, data);

const createFacturaComplementaria = (data: IFacturaComplementariaPayload) => API.post('/facturas-complementarias', data);
const editFacturaComplementaria = (_id: string, data: IFacturaComplementariaPayload) => API.put(`/facturas-complementarias/${_id}`, data);

const createFacturaRectificativa = (data: IFacturaRectificativaPayload) => API.post('/facturas-rectificativas', data);
const editFacturaRectificativa = (_id: string, data: IFacturaRectificativaPayload) => API.put(`/facturas-rectificativas/${_id}`, data);

const createFacturaRectificativaCore = (data: IRectificativaPayload) => API.post('/facturas-core-rectificativas', data);

const getServiciosNoFacturables = (userId: string) => API.get<INoFacturable[]>(`/serviceInvoice/no-facturables/${userId}`);
const getServiciosSinFacturar = (userId: string) => API.get<ISinFacturar[]>(`/serviceInvoice/sin-facturar/${userId}`);
const getFacturas = (userId: string) => API.get<IFactura[]>(`/facturas/${userId}`);
const getFacturasParciales = (userId: string) => API.get<IFactura[]>(`/facturas-parciales/${userId}`);

const getFacturacion = (userId: string) =>  API.get<FacturaInforme[]>(`/facturacion/${userId}`);

const sendFactura  = (data: any) => API.post('/sendFactura', data);

// onProgreso: bytes del ZIP recibidos hasta ahora (el servidor lo envía desde el primer PDF).
const descargarFacturas = (data: { userId: string; facturaIds: string[] }, onProgreso?: (evento: AxiosProgressEvent) => void) =>
  API.post<Blob>('/facturas/descargar', data, { responseType: 'blob', onDownloadProgress: onProgreso });
// fechaInicio / fechaFin: el día elegido en el calendario, "YYYY-MM-DD" (el servidor lo interpreta en la zona de la clínica)
const descargarResumen = (data: { userId: string; fechaInicio: string; fechaFin: string; moneda: string; entidadId?: string | null }) => API.post<Blob>('/facturas/resumen', data, { responseType: 'blob' });
const getFacturaById = (facturaId: string) => API.get(`/facturaComplementaria/${facturaId}`);


const getFacturacionClase = (masterClassId: string, pacienteIds: string[], fechaClase: string) => API.get(`/facturacion/clase/${masterClassId}?pacientes=${pacienteIds.join(',')}&fechaClase=${fechaClase}`);
const saveFacturacionClase = (config: IServiceClassInvoice) => API.post('/facturacion/clase/config', config);
const getEstadoFacturacionMes = ({ month, year, actividadId, especialistaId }: { month: number; year: number; actividadId?: number; especialistaId?: number }) => API.get(`/facturacion/clase/estado-mes?month=${month}&year=${year}${actividadId !== undefined ? `&actividadId=${actividadId}` : ''}${especialistaId !== undefined ? `&especialistaId=${especialistaId}` : ''}`);

const createFacturaClase = (data: IFacturaClasePayload) => API.post('/facturas/clase', data);
const editFacturaClase = (_id: string, data: any) => API.put(`/facturas/clase/${_id}`, data);
const getFacturaClaseById = (facturaId: string) => API.get(`/facturas/clase/${facturaId}`);
const updateMotivoNoFacturableClase = (
  pacienteId: string,
  actividadId: number,
  periodo: string,
  motivoNoFacturable: string,
) =>
  API.put(
    `/facturacion/clase/${pacienteId}/${actividadId}/${periodo}`,
    { motivoNoFacturable }
);

const createFacturasClase = (data: { userId: string; masterClassId: string; month: number; year: number; entityId?: string | null; }) => API.post('/facturas/clase/mes', data);

const createFacturaManual = (data: IFacturaManualPayload) => API.post('/facturas/manual', data);
const getFacturaManualById = (facturaId: string) => API.get(`/facturas/manual/${facturaId}`);
const editFacturaManual = (_id: string, data: any) => API.put(`/facturas/manual/${_id}`, data);

const anularFactura = (_id: string, data: { motivoAnulacion: string, tipoPago: string, devolverSesiones?: boolean, estadoEsperado?: string }) => API.patch(`/facturas/${_id}/anular`, data);
const cambiarEntidadFactura = (_id: string, data: { userId: string, entidadNuevaId: string | null, estadoEsperado?: string }) => API.patch(`/facturas/${_id}/cambiar-entidad`, data);

const FacturacionService = {
    getInvoiceCounter,
    getInvoiceCounters,
    createInvoiceCounter,
    updateInvoiceCounter,
    checkActiveServiceInvoice,
    checkOriginalServiceInvoice,
    marcarNoFacturable,
    updateMotivoNoFacturable,
    createFactura,
    editFactura,
    pagarFactura, 
    emitirFactura,
    eliminarFactura,
    getNumeroOrientativo,
    getServiciosNoFacturables,
    getServiciosSinFacturar,
    getFacturas,
    getFacturasParciales,
    getFacturacion,
    sendFactura,
    descargarFacturas,
    descargarResumen,
    createFacturaComplementaria,
    editFacturaComplementaria,
    createFacturaRectificativa,
    editFacturaRectificativa,
    createFacturaRectificativaCore,
    getFacturaById,
    getFacturacionClase,
    saveFacturacionClase,
    getEstadoFacturacionMes,
    createFacturaClase,
    getFacturaClaseById,
    editFacturaClase,
    updateMotivoNoFacturableClase,
    createFacturasClase,
    createFacturaManual,
    getFacturaManualById,
    editFacturaManual,
    anularFactura,
    cambiarEntidadFactura
};

export default FacturacionService;